import dayjs from "dayjs";
import SVG from "react-inlinesvg";

import defaultAuthor from "@/constants/defaultAuthor";
import siteInfo from "@/constants/siteInfo";
import { defaultAuthorMap } from "@/utils/langCodeMapper";

const getAuthorDetails = (item) => {
  const defaultAuthorState =
    item?.langcode &&
    item?.langcode in defaultAuthorMap &&
    defaultAuthorMap[item?.langcode];

  const authorLink = item?.author?.[0]?.canonical_url
    ? item.author[0].canonical_url
    : defaultAuthorState
      ? defaultAuthorState.link
      : defaultAuthor.canonical_url;

  const authorTitle =
    (item?.author?.[0]?.title ||
      (item?.author_detail?.[0]?.title !== "mediology"
        ? item?.author_detail?.[0]?.title
        : defaultAuthorState?.name) ||
      defaultAuthorState?.name) ?? defaultAuthor.title;

  return { authorLink, authorTitle };
};

const getImageUrl = (item, size) => {
  const image = item?.image_small || item?.thumb_image;

  if (!image) {
    return null;
  }

  return `${image}${image.includes("?") ? "&" : "?"}size=${size}`;
};

const MetaLine = ({ item, compact = false }) => {
  const { authorLink, authorTitle } = getAuthorDetails(item);
  const dateValue = item?.datetime_published || item?.publishtime || item?.datetime;

  return (
    <p className={`text-xs text-gray-500 font-extralight mt-1 ${compact ? "line-clamp-1" : ""}`}>
      <img
        className="h-[16px] w-[16px] inline-block mr-1 align-text-bottom"
        alt="read and video icon"
        loading="lazy"
        height={16}
        width={16}
        src={
          item?.content_type === "video"
            ? "https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/watchType.svg"
            : "https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/readType.svg"
        }
      />
      <time dateTime={dateValue}>{dayjs(dateValue).format("MMM DD YYYY")}</time> · By{" "}
      <a href={authorLink} target={item?.publishtime ? "_blank" : "_self"}>
        <span className="text-primary">{authorTitle}</span>
      </a>
    </p>
  );
};

const MediaThumb = ({ item, href, size, imageClass, iconClass, eager = false }) => {
  const imageUrl = getImageUrl(item, size);

  return (
    <div className="relative overflow-hidden rounded-md bg-gray-100 shrink-0">
      <a href={href} target={item?.publishtime ? "_blank" : "_self"} className="block h-full w-full">
        {imageUrl ? (
          <img
            className={`block  object-cover ${imageClass}`}
            src={imageUrl}
            title={item?.image_title}
            alt={item?.image_small_alt_text || item?.image_title || siteInfo?.siteName}
            fetchPriority={eager ? "high" : "auto"}
            loading={eager ? "eager" : "lazy"}
            role="button"
          />
        ) : (
          <div className={imageClass} />
        )}
      </a>

      {item?.content_type === "video" && (
        <a href={href} target={item?.publishtime ? "_blank" : "_self"}>
          <div className="absolute inset-0 flex justify-center items-center">
            <SVG
              className={iconClass}
              src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/play-media.svg"
            />
          </div>
        </a>
      )}
    </div>
  );
};

const FeaturedCard = ({ item, isLCP = false }) => {
  const href = item?.canonical_url ?? "";

  return (
    <article className="flex flex-col overflow-hidden min-h-full">
      <MediaThumb
        item={item}
        href={href}
        size="360:203"
        imageClass="w-full aspect-[16/9]"
        iconClass="w-10 h-10"
        eager={isLCP}
      />

      <div className="pt-2">
        <a href={href} target={item?.publishtime ? "_blank" : "_self"}>
          <h3
            className="text-lg font-bold line-clamp-3 hover:text-primary"
            dangerouslySetInnerHTML={{ __html: item?.title_short || item?.title || "" }}
          />
        </a>
        <MetaLine item={item} />
      </div>
    </article>
  );
};

const CompactCard = ({ item }) => {
  const href = item?.canonical_url ?? "";

  return (
    <article className="flex rounded-sm overflow-hidden items-start min-h-[96px]">
      <MediaThumb
        item={item}
        href={href}
        size="150:84"
        imageClass="   w-[150px] h-[84px] aspect-[16/9]"
        iconClass="w-8 h-8"
      />

      <div className="pl-2 flex-1 min-h-[96px]">
        <a href={href} target={item?.publishtime ? "_blank" : "_self"}>
          <h4
            className="text-sm font-bold hover:text-primary line-clamp-3"
            dangerouslySetInnerHTML={{ __html: item?.title_short || item?.title || "" }}
          />
        </a>
        <MetaLine item={item} compact />
      </div>
    </article>
  );
};

const ContentGrid = ({ content = [], isLCP = false, heading = null }) => {
  const items = Array.isArray(content) ? content.slice(0, 6) : [];
 

  return (
    <div className="my-2 mx-4">

      {heading && <h1 className="flex text-xl font-semibold  my-4">
        <span className="w-2 h-[auto] bg-primary inline-block mr-2"></span>
        {heading}
      </h1>}
    { items?.length > 0 && <div className="flex flex-col md:flex-row gap-2 md:items-start">
        <div className="w-full md:w-1/2">
          {items[0] && <FeaturedCard item={items[0]} isLCP={isLCP} />}
          {items[1] && (
            <div className="mt-2">
              <CompactCard item={items[1]} />
            </div>
          )}
        </div>

        <div className="w-full md:w-1/2 md:overflow-auto space-y-2">
          {items.slice(2).map((item, index) => (
            <CompactCard key={item?.nid || item?.canonical_url || index} item={item} />
          ))}
        </div>
      </div>}
    </div>
  );
};

export default ContentGrid;
